import {View} from "react-native";
import {BlockParticipantRequest, EditParticipantRequest, EventInviteRequest, EventRoles, Participating} from "@/lib/types";
import {useLocalSearchParams, useRouter} from "expo-router";
import {fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/components/Page";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import Header from "@/lib/components/Header";
import Badge from "@/lib/components/Badge";
import {useEffect, useMemo, useState} from "react";
import Radio from "@/lib/components/Radio";
import ActionButton from "@/lib/components/ActionButton";
import ErrorMessage from "@/lib/components/ErrorMessage";
import {useEvent} from "@/lib/components/EventProvider";
import Split from "@/lib/components/Split";
import {Color} from "@/lib/styles";
import Message from "@/lib/components/Message";
import Nav from "@/lib/components/Nav";

export default function RoleEditPage() {
    const {username} = useLocalSearchParams();
    const {session} = useSession();
    const [roles, setRoles] = useState<EventRoles | null>(null);
    const [busy, setBusy] = useState(false);
    const [blocking, setBlocking] = useState(false);
    const [saveError, setSaveError] = useState<Error | null>(null);
    const router = useRouter();

    const {event, eventLoading, eventError, refreshEvent} = useEvent();

    const subject = event?.participants.find(p => p.avatar.username === username) || null;

    useEffect(() => {
        if (!subject) {
            return;
        }
        setRoles({
            is_host: subject.status === Participating.HOST,
            is_moderator: subject.status === Participating.MODERATOR,
            is_blocked: subject.status === Participating.BLOCKED,
            can_invite: subject.can_invite,
            can_post: subject.can_post,
        });
    }, [subject])

    const header = useMemo(() => {
        if (!subject || !event) {
            return null;
        }
        return (
            <View style={{gap: 8}}>
                <Header title={subject.avatar.gecos} icon={"edit"}/>

                <View style={{alignItems: "flex-start"}}>
                    <Badge title={event.title}
                           color={event.color}
                           contrastColor={event.contrast_color}
                    />
                </View>
            </View>
        )
    }, [subject, event]);

    if (eventError) {
        return <ErrorPage error={eventError}/>
    } else if (eventLoading || !event || !roles) {
        return <SpinnerPage/>
    }

    if (!subject) {
        const msg = `Bruker med navn "${username}" er ikke registrert på eventet.`
        return <ErrorPage error={new Error(msg)}/>
    }

    async function saveRoles() {
        if (!event || !subject || !roles) {
            return;
        }
        setSaveError(null);
        setBusy(true);
        let payload: EditParticipantRequest = {
            user_id: subject.avatar.id,
            roles,
        }
        fetchBackendNoContent(`/event/${event.event_id}/participant`, "PUT", session, payload).then(async () => {
            await refreshEvent();
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    async function unblockUser() {
        if (!event || !subject) {
            return;
        }
        setSaveError(null);
        setBusy(true);
        const payload = new EventInviteRequest([subject.avatar.id], []);
        fetchBackendNoContent(`/event/${event.event_id}/invite`, "POST", session, payload).then(async () => {
            await refreshEvent();
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    async function blockUser() {
        if (!event || !subject) {
            return;
        }
        setSaveError(null);
        setBlocking(true);
        let payload: BlockParticipantRequest = {
            user_id: subject.avatar.id,
        }
        fetchBackendNoContent(`/event/${event.event_id}/participant`, "DELETE", session, payload).then(async () => {
            await refreshEvent();
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBlocking(false);
        });
    }

    if (subject.status !== Participating.BLOCKED) {
        return (
            <Page scrollable={true}>
                <View style={{gap: 24}}>

                    {header}

                    <View style={{gap: 8}}>
                        <Header title={"Angi rolle"}/>
                        <Radio selected={roles.is_host}
                               onPress={() => setRoles({...roles, is_host: true, is_moderator: false})}
                               title={"Arrangør"}
                               description={"Har alle rettigheter og kan i tillegg bestemme roller."}
                               checkbox={false}
                        />
                        <Radio selected={roles.is_moderator}
                               onPress={() => setRoles({...roles, is_host: false, is_moderator: true})}
                               title={"Moderator"}
                               description={"Kan invitere og utestenge, starte diskusjoner, samt redigere eventet."}
                               checkbox={false}
                        />
                        <Radio selected={!roles.is_host && !roles.is_moderator}
                               onPress={() => setRoles({...roles, is_host: false, is_moderator: false})}
                               title={"Deltaker"}
                               description={"Ingen ekstra rettigheter utover eventets innstillinger."}
                               checkbox={false}
                        />
                    </View>

                    <View style={{gap: 8}}>
                        <Header title={"Ekstra tilganger"}/>
                        <Radio selected={roles.can_invite}
                               onPress={() => setRoles({...roles, can_invite: !roles.can_invite})}
                               title={"Invitasjon"}
                               description={"Deltakeren kan invitere venner til eventet"}
                               checkbox={true}
                        />
                        <Radio selected={roles.can_post}
                               onPress={() => setRoles({...roles, can_post: !roles.can_post})}
                               title={"Chat"}
                               description={"Deltakeren kan starte diskusjonstråder"}
                               checkbox={true}
                        />
                    </View>

                    {saveError && <ErrorMessage error={saveError}/>}

                    <Split left={
                        <ActionButton onPress={blockUser} icon={"cancel"} title={"Blokker"} busy={blocking} color={Color.Red}/>
                    } right={
                        <ActionButton onPress={saveRoles} icon={"checkmark"} title={"Lagre"} busy={busy}/>
                    }/>
                </View>
            </Page>
        )
    }

    return (
        <Page scrollable={true}>
            <View style={{gap: 24}}>

                {header}

                <View style={{gap: 8}}>
                    <Header title={"Brukeren er blokkert"}/>
                    <Message text={"Du kan fjerne blokkeringen for å invitere vedkommende til arrangementet."}/>
                </View>

                {saveError && <ErrorMessage error={saveError}/>}

                <Nav>
                    <ActionButton onPress={unblockUser} icon={"cancel"} title={"Fjern blokkering"} busy={busy}/>
                </Nav>
            </View>
        </Page>
    )
}